import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_interest: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service_interest: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Unable to submit form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="contact-page" className="min-h-screen bg-gray-50 flex items-center justify-center py-20">
        <div className="max-w-md mx-auto px-6 text-center">
          <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Thank You!
          </h2>
          <p className="text-gray-600 mb-8">
            Your message has been received. We will contact you within 24-48 hours to discuss your business needs.
          </p>
          <Button 
            onClick={() => setSubmitted(false)}
            className="bg-[#1E3A5F] hover:bg-[#162c47] text-white"
          >
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-[#9D9045] font-medium text-sm tracking-wider uppercase">Get In Touch</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Contact Us
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Ready to start your business journey? Schedule a free discovery consultation today.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold text-[#1E3A5F] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Send Us a Message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="h-12"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="h-12"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className="h-12"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Service Interest</Label>
                    <Select onValueChange={handleServiceChange}>
                      <SelectTrigger className="h-12" data-testid="contact-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="business-coaching">Business Strategy & Coaching</SelectItem>
                        <SelectItem value="business-startup">Business Startup</SelectItem>
                        <SelectItem value="money-management">Money Management Coaching</SelectItem>
                        <SelectItem value="registered-agent">Registered Agent</SelectItem>
                        <SelectItem value="design">Logo & Product Design</SelectItem>
                        <SelectItem value="notary">Mobile Notary Services</SelectItem>
                        <SelectItem value="business-school">Business School Courses</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your business needs..."
                    rows={5}
                    data-testid="contact-message-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-[#1E3A5F] hover:bg-[#162c47] text-white py-6 text-lg"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#1E3A5F] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Contact Information
                </h2>
                <p className="text-gray-600 mb-8">
                  We're here to help you succeed. Reach out to us through any of the methods below, or fill out the contact form and we'll get back to you within 24-48 hours.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="h-12 w-12 rounded-lg bg-[#1E3A5F]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1E3A5F]">Phone</h4>
                    <a href="tel:+18772955905" className="text-gray-600 hover:text-[#9D9045]">
                      (877) 295-5905
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="h-12 w-12 rounded-lg bg-[#1E3A5F]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1E3A5F]">Email</h4>
                    <a href="mailto:omwbusiness@consultant.com" className="text-gray-600 hover:text-[#9D9045]">
                      omwbusiness@consultant.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="h-12 w-12 rounded-lg bg-[#1E3A5F]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1E3A5F]">Address</h4>
                    <p className="text-gray-600">
                      4049 US Highway 231 #1003<br />
                      Wetumpka, Alabama
                    </p>
                    <p className="text-sm text-gray-500 mt-1">Central Alabama Service Area</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="h-12 w-12 rounded-lg bg-[#1E3A5F]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1E3A5F]">Hours of Operation</h4>
                    <div className="text-gray-600 text-sm space-y-1">
                      <p>Monday - Friday: 9:00 AM - 5:00 PM</p>
                      <p>Saturday: 11:00 AM - 2:00 PM</p>
                      <p>Sunday: Closed</p>
                      <p className="text-[#9D9045] font-medium">Weekend after-hours by appointment</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Free Consultation Badge */}
              <div className="bg-[#9D9045]/10 border border-[#9D9045]/30 rounded-xl p-6 text-center">
                <h4 className="font-semibold text-[#1E3A5F] mb-2">Free Discovery Consultation</h4>
                <p className="text-gray-600 text-sm">
                  Not sure where to start? Schedule a free consultation to discuss your business goals and how we can help.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
